import { apiFetch, readErrorMessage } from '../api/client';
import { documentEditorHref } from '../documentEditor/documentEditorUrls';
import type { DocumentEditorKind } from '../documentEditor/documentFileTypes';
import { USER_STORAGE_MOUNT_ID } from '../storageFileAudit/formatters';
import {
  joinContentFolderPath,
  readDefaultContentFolderPath
} from './defaultContentFolderPrefs';
import { ensureUserStorageFolder } from './ensureUserStorageFolder';
import { mountApiBase } from './fileBrowserApi';
import {
  blankOfficeFile,
  getNewOfficeFileMeta,
  type NewOfficeFileKind
} from './fileBrowserNewFile';

const MAX_NAME_ATTEMPTS = 50;

export type CreatedBlankOfficeFile = {
  mountId: string;
  relativePath: string;
  fileName: string;
  editorKind: DocumentEditorKind;
  editorHref: string;
};

/** Builds Document.docx / Workbook.xlsx, then Document (2).docx, … */
export function nextOfficeFileName(kind: NewOfficeFileKind, attempt: number): string {
  const meta = getNewOfficeFileMeta(kind);
  const base = meta.defaultName.slice(0, -meta.extension.length);
  if (attempt <= 1) return meta.defaultName;
  return `${base} (${attempt})${meta.extension}`;
}

/**
 * Creates a blank Office file in the signed-in user's My Storage default content folder
 * and returns mount/path/editor metadata (My Apps launchers open via openDocumentEditorSession).
 */
export async function createBlankOfficeInUserStorage(
  kind: NewOfficeFileKind,
  mountId: string = USER_STORAGE_MOUNT_ID,
  folderPath?: string
): Promise<CreatedBlankOfficeFile> {
  const meta = getNewOfficeFileMeta(kind);
  const folder = await ensureUserStorageFolder(
    folderPath ?? readDefaultContentFolderPath(),
    mountId
  );
  let lastError = 'Could not create file.';

  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt++) {
    const fileName = nextOfficeFileName(kind, attempt);
    const file = blankOfficeFile(kind, fileName);
    const form = new FormData();
    form.append('file', file);
    const uploadPath = folder ? encodeURIComponent(folder) : '';
    const res = await apiFetch(`${mountApiBase(mountId)}/upload?path=${uploadPath}`, {
      method: 'POST',
      body: form
    });

    if (res.ok) {
      const relativePath = joinContentFolderPath(folder, fileName);
      return {
        mountId,
        relativePath,
        fileName,
        editorKind: meta.editorKind,
        editorHref: documentEditorHref(meta.editorKind, 'edit', mountId, relativePath)
      };
    }

    if (res.status === 409) {
      lastError = `A file named "${fileName}" already exists.`;
      continue;
    }

    throw new Error(await readErrorMessage(res));
  }

  throw new Error(lastError);
}
